import React, {useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from "../../utils/Hooks";
import {Link, useNavigate, useParams} from "react-router-dom";
import {LoggedInView} from "../common/LoggedInView";
import {BreadcrumbLink} from "../../models/BreadcrumbLink";
import {getCurrentInquiry} from "../../store/reducers/InquiriesSlice";
import {getCurrentOrder} from "../../store/reducers/OrdersSlice";
import OrderComponent from "../../models/OrderComponent";
import Breadcrumbs from "../common/Breadcrumbs";
import Loadings, {DEFAULT_CONTACT} from "./components/Loadings";
import ElogSelect, {Option} from "../common/form/ElogSelect";
import {createOrder, resetCreateOrderStatus} from "../../store/reducers/OrdersSlice";
import {RequestStatus} from "../../utils/Enums";
import {apiClient} from "../../utils/ApiClient";
import Loading, {LoadingType} from "../../models/Loading";
import OrderForm from "./components/OrderForm";
import {FormikProvider, useFormik, Form} from "formik";
import {format} from 'date-fns';
import {getCopiedOrderInitialValues} from "./utils/getCopiedOrderInitialValues";

interface NewOrderScreenProps {}
export interface NewOrderFormProps {
    user: number;
    expires_at: string;
}

type SubmissionMode = 'draft' | 'order' | null;

export const NewOrderScreen: React.FC<NewOrderScreenProps> = () => {
    const {id, type} = useParams();
    const isCopy = type === 'copy';
    const isOrderSource = type === 'parent' || isCopy;
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const ordersState = useAppSelector((state) => state.orders);
    const inquiriesState = useAppSelector((state) => state.inquiries);
    const usersState = useAppSelector((state) => state.users);
    const countriesState = useAppSelector(state => state.countries);
    const infoState = useAppSelector(state => state.info);
    const requestStatus: RequestStatus = ordersState.createOrderStatus;
    const [orderComponents, setOrderComponents] = useState<OrderComponent[]>([]);
    const [selectedService, setSelectedService] = useState<string | null>(null);
    const [currentService, setCurrentService] = useState<any | null>(null);
    const [initialValues, setInitialValues] = useState<any | null>(null);
    const [initialValuesLoaded, setInitialValuesLoaded] = useState(false);
    const [submissionMode, setSubmissionMode] = useState<SubmissionMode>(null);
    const storageKey = `newOrder:${id}:${type}`;
    const resetStorage = () => { sessionStorage.removeItem(storageKey); };
    const breadcrumbLinks: BreadcrumbLink[] = [
        {text: isOrderSource ? "Veod" : "Päringud", url: isOrderSource ? "/orders" : "/inquiries"},
        {text: isCopy ? "Veo kopeerimine" : "Veo lisamine", url: `/orders/new/${id}/${type}`},
    ];

    useEffect(() => {
        if (id) {
            console.log("[INITIAL_VALUES_DEBUG]: Dispatching data fetch with id:", parseInt(id));
            if (type === 'inquiry') {
                dispatch(getCurrentInquiry({id: parseInt(id)}));
            } else if (isOrderSource) {
                dispatch(getCurrentOrder({id}));
            }
        }
    }, [id, dispatch, type]);

    useEffect(() => {
        console.log("[INITIAL_VALUES_DEBUG]: State changed, checking current data source based on type:", type);

        let currentData: any = null;
        if (type === 'inquiry') {
            currentData = inquiriesState?.currentInquiry;
        } else if (isOrderSource) {
            currentData = ordersState?.currentOrder;
        }

        if (currentData && id && currentData.id === parseInt(id)) {
            if (currentData?.service?.id) {
                console.log("[INITIAL_VALUES_DEBUG]: Setting selectedService to:", String(currentData?.service?.id));
                setSelectedService(String(currentData?.service?.id));
            }

            const savedRaw = sessionStorage.getItem(storageKey);
            let initial = null;

            if (savedRaw && savedRaw !== "null") {
                try {
                    const parsed = JSON.parse(savedRaw);
                    console.log("[INITIAL_VALUES_DEBUG SET 1]: Using saved data from sessionStorage for initialValues", parsed);
                    initial = parsed;
                } catch (error) {
                    console.error("[INITIAL_VALUES_DEBUG SET 2]: Error parsing sessionStorage, using default values", getDefaultInitialValues(currentData));
                    initial = getDefaultInitialValues(currentData);
                }
            } else {
                console.log("[INITIAL_VALUES_DEBUG SET 3]: No valid saved data in sessionStorage, using default values", getDefaultInitialValues(currentData));
                initial = getDefaultInitialValues(currentData);
            }

            setInitialValues(initial);
            console.log("[INITIAL_VALUES_DEBUG SET 4]: initialValues set to:", initial);
            setInitialValuesLoaded(true);
        } else if (!currentData) {
            setInitialValues(null);
            setInitialValuesLoaded(false);
        }
    }, [inquiriesState?.currentInquiry, ordersState?.currentOrder, storageKey, id, type]);

    useEffect(() => {
        if (requestStatus === RequestStatus.Success) {
            console.log("[INITIAL_VALUES_DEBUG]: createOrderStatus is Success, navigating to /orders");
            dispatch(resetCreateOrderStatus());
            resetStorage();
            navigate('/orders');
        } else if (requestStatus === RequestStatus.Failed) {
            setSubmissionMode(null);
        }
    }, [requestStatus, dispatch, navigate, storageKey]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                console.log("[INITIAL_VALUES_DEBUG]: Fetching service details for serviceId:", selectedService);
                const response = await apiClient.get(`services/${selectedService}`);
                const data = response.data;
                console.log("[INITIAL_VALUES_DEBUG]: Fetched service data:", data);
                setOrderComponents(data?.order_components_hierarchical || []);
                setCurrentService(data);
            } catch (error) {
                console.error('[INITIAL_VALUES_DEBUG]: Error fetching service data:', error);
            }
        };

        if (selectedService !== null) {
            fetchData();
        }
    }, [selectedService]);

    const getDefaultInitialValues = (currentData: any = inquiriesState?.currentInquiry) => {
        console.log("[INITIAL_VALUES_DEBUG]: getDefaultInitialValues called for", type);

        const userOptions = usersState.users.filter((user) => user.is_checker === 1);
        const defaultChecker = userOptions[0]?.id?.toString() ?? '';

        if (isCopy) {
            return getCopiedOrderInitialValues(currentData, defaultChecker, DEFAULT_CONTACT);
        }

        const checkerValue = type === 'parent' ? (currentData?.checker_id != null ? String(currentData.checker_id) : defaultChecker) : defaultChecker;

        console.log('CheckerValue', checkerValue);

        return {
            checker: checkerValue,
            client: currentData?.client?.name ?? '',
            vendor: type === 'parent' ? currentData?.vendor?.name ?? '' : '',
            service: String(currentData?.service?.id ?? ''),
            amount: currentData?.amount ?? '',
            meters: currentData?.meters ?? '',
            weight: currentData?.weight ?? '',
            buySum: type === 'parent' ? 0 : currentData?.buy_sum ?? '',
            saleSum: type === 'parent' ? 0 : currentData?.sale_sum ?? '',
            orderTime: format(new Date(), 'dd.MM.yyyy'),
            loadingTime: format(new Date(), 'dd.MM.yyyy'),
            clientContact: type === 'parent' ? (currentData?.client_contacts ?? []).map((c: any) => String(c?.id ?? 0)) : [String(currentData?.client_contact?.id ?? 0)],
            vendorContact: type === 'parent' ? (currentData?.vendor_contacts ?? []).map((c: any) => String(c?.id ?? 0)) : [0],
            buyInvoice: '',
            saleInvoice: '',
            buyPaid: false,
            salePaid: false,
            website: false,
            cmr: false,
            reclamation: false,
            refNumber: '',
            carNumber: '',
            terminal: '',
            loadingInstructions: '',
            unloadingInstructions: '',
            comment: currentData?.comment ?? '',
            loadings:
                type === 'parent'
                    ? (currentData?.loadings ?? []).map((l: any) => ({
                        ...l,
                        country: l.country_id ?? '',
                        contacts: l.contacts?.length > 0 ? l.contacts : [DEFAULT_CONTACT],
                        company: l.company ?? '',
                        address: l.address ?? '',
                        city: l.city ?? '',
                        deleted: l.deleted ?? false,
                        type: LoadingType.Loading
                    }))
                    : [{
                        country: currentData?.start_country_id ?? currentData?.loadings?.[0]?.country_id,
                        contacts: [DEFAULT_CONTACT],
                        order_id: 0,
                        type: LoadingType.Loading,
                        city: currentData?.start_city ?? currentData?.loadings?.[0]?.city ?? '',
                    }],
            unloadings:
                type === 'parent'
                    ? (currentData?.unloadings ?? []).map((u: any) => ({
                        ...u,
                        country: u.country_id ?? '',
                        contacts: u.contacts?.length > 0 ? u.contacts : [DEFAULT_CONTACT],
                        company: u.company ?? '',
                        address: u.address ?? '',
                        city: u.city ?? '',
                        deleted: u.deleted ?? false,
                        type: LoadingType.Unloading
                    }))
                    : [{
                        country: currentData?.destination_country_id ?? currentData?.unloadings?.[0]?.country_id,
                        contacts: [DEFAULT_CONTACT],
                        order_id: 0,
                        type: LoadingType.Unloading,
                        city: currentData?.destination_city ?? currentData?.unloadings?.[0]?.city ?? '',
                    }],
            loaded: false,
            unloaded: false,
        };
    };

    const userOptions: Option[] = usersState.users
        .filter((user) => user.is_checker === 1)
        .map((user) => ({
            value: user.id.toString(),
            label: `${user.firstname} ${user.lastname}`,
        }));

    const countryOptions: Option[] = countriesState.countries.map((country) => ({
        value: country.id.toString(),
        label: country.name,
    }));

    const hasLoadings = (orderComponents.findIndex(component => component.component === "Loadings") > -1);

    const formik = useFormik({
        initialValues: initialValues || {},
        onSubmit: (values: any) => {
            if (id && type) {
                console.log("[INITIAL_VALUES_DEBUG]: Form submitted with values:", values);
                dispatch(createOrder({values: values, id: parseInt(id), type: type}));
                resetStorage();
            }
        },
        enableReinitialize: true,
    });

    const {values, setFieldValue, handleSubmit} = formik;
    const isSubmitting = submissionMode !== null || requestStatus === RequestStatus.Loading;

    useEffect(() => {
        const insuranceCommission = infoState.settings?.commission ?? infoState.settings?.comission;
        const commissionPercentage = parseFloat(String(insuranceCommission).replace(',', '.'));
        const saleSumNum = parseFloat(String(values?.saleSum ?? '').replace(/\s/g, '').replace(',', '.'));

        const preloadService = isOrderSource ? ordersState?.currentOrder?.service : inquiriesState?.currentInquiry?.service;
        const serviceObj = currentService || preloadService || null;

        const isInsurance = serviceObj?.is_insurance === 1 || serviceObj?.is_insurance === true || serviceObj?.is_insurance === '1';
        const serviceIdStr = String(serviceObj?.id ?? '');

        if (
            isInsurance &&
            !Number.isNaN(commissionPercentage) &&
            !Number.isNaN(saleSumNum) &&
            serviceIdStr !== '11'
        ) {
            const buySum = (saleSumNum * (1 - commissionPercentage / 100)).toFixed(2);
            formik.setFieldValue('buySum', buySum);
        }

        const vendorStr = typeof values?.vendor === 'string' ? values.vendor : String(values?.vendor?.label || values?.vendor?.name || '');
        if (!isCopy && serviceIdStr === '11' && vendorStr.toLowerCase().includes('ets logistic')) {
            formik.setFieldValue('buySum', 0);
            formik.setFieldValue('buyInvoice', 'puudub');
            formik.setFieldValue('buyPaid', true);
        }
    }, [values?.saleSum, values?.service, values?.vendor, infoState.settings, currentService, type, formik.setFieldValue, inquiriesState?.currentInquiry?.service, ordersState?.currentOrder?.service]);

    useEffect(() => {
        console.log("[INITIAL_VALUES_DEBUG]: values in formik changed:", values);
        if (initialValuesLoaded && values && Object.keys(values).length > 0) {
            sessionStorage.setItem(storageKey, JSON.stringify(values));
        }
    }, [values, storageKey, initialValuesLoaded]);

    return (
        <LoggedInView fullWidth={false}>
            <Breadcrumbs links={breadcrumbLinks}/>
            {requestStatus === RequestStatus.Failed && ordersState.errors.length > 0 && (
                <div className="message msg-error">
                    <div>
                        <h2>Viga:</h2>
                        {ordersState.errors.map((error, index) => (
                            <span key={index}>{error}<br/></span>
                        ))}
                    </div>
                </div>
            )}
            {initialValues !== null ? (
                <FormikProvider value={formik}>
                    <Form onSubmit={handleSubmit}>
                        <h1>{isCopy ? 'Veo kopeerimine' : 'Veo lisamine'}</h1>
                        {hasLoadings && values?.loadings && values?.unloadings && (
                            <Loadings
                                countries={countryOptions}
                                orderComponents={orderComponents[orderComponents.findIndex(component => component.component === "Loadings")].children ?? []}
                                orderLoadings={values.loadings}
                                orderUnloadings={values.unloadings}
                            />
                        )}
                        <div className="form">
                            <h2>
                                <span>Veo andmed</span>
                            </h2>
                            <table className="form">
                                <OrderForm
                                    orderComponents={orderComponents}
                                    taxDept={isOrderSource
                                        ? ordersState?.currentOrder?.tax_dept == 1
                                        : inquiriesState?.currentInquiry?.tax_dept == 1}
                                    clientContacts={isOrderSource
                                        ? (ordersState?.currentOrder?.client_contacts ?? []).map(contact => ({
                                            value: String(contact.id),
                                            label: contact.name ?? '',
                                        }))
                                        : inquiriesState?.currentInquiry?.client_contact ? [{
                                            value: (inquiriesState?.currentInquiry?.client_contact?.id ?? '0').toString(),
                                            label: inquiriesState?.currentInquiry?.client_contact?.name ?? ''
                                        }] : []}
                                    vendorContacts={isOrderSource
                                        ? (ordersState?.currentOrder?.vendor_contacts ?? []).map(contact => ({
                                            value: String(contact.id),
                                            label: contact.name ?? '',
                                        }))
                                        : []}
                                    files={isCopy ? ordersState?.currentOrder?.files ?? [] : []}
                                    onSelectedServiceChange={(service) => {
                                        setSelectedService(service);
                                    }}
                                    isModify={false}
                                    hideLogistician={true}
                                />
                            </table>
                        </div>
                        {requestStatus === RequestStatus.Failed && ordersState.errors.length > 0 && (
                            <div className="message msg-error">
                                <div>
                                    <h2>Viga:</h2>
                                    {ordersState.errors.map((error, index) => (
                                        <span key={index}>{error}<br/></span>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="action clear">
                            <p>
                                <button
                                    type="button"
                                    style={{
                                        backgroundColor: 'gray',
                                        backgroundImage: 'none',
                                        color: 'white',
                                        marginRight: '1rem'
                                    }}
                                    onClick={async () => {
                                        setSubmissionMode('draft');
                                        await setFieldValue("draft", true);
                                        handleSubmit();
                                    }}
                                >
                                    <span style={{ backgroundImage: 'none', background: 'gray', color: 'white' }}>
                                        <strong style={{ backgroundImage: 'none', background: 'gray', color: 'white' }}>
                                            {isCopy ? 'Mustand' : 'Salvesta mustand'}
                                        </strong>
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    style={{marginRight: '1rem'}}
                                    onClick={async () => {
                                        setSubmissionMode('order');
                                        await setFieldValue("draft", false);
                                        handleSubmit();
                                    }}
                                >
                                    <span>
                                        <strong>{isCopy ? 'Veoks' : 'Sisesta vedu'}</strong>
                                    </span>
                                </button>
                                <Link to="#" onClick={() => {
                                    resetStorage();
                                    window.history.back();
                                }}>Tühista</Link>
                            </p>
                        </div>
                        {isSubmitting && (
                            <div className="order-submit-overlay" role="status" aria-live="polite" aria-label="Salvestan">
                                <div className="order-submit-overlay__content">
                                    <span className="order-submit-overlay__spinner" aria-hidden="true"/>
                                    <strong>Salvestan...</strong>
                                </div>
                            </div>
                        )}
                    </Form>
                </FormikProvider>
            ) : (
                <div>Laadimine...</div>
            )}
        </LoggedInView>
    );
}
